import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, Keyboard } from 'lucide-react';

export const BarcodeScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [mode, setMode] = useState('camera'); // camera | manual

  useEffect(() => {
    if (mode !== 'camera') return;
    
    let scanner = null;
    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode('barcode-scanner-container');
        scannerRef.current = scanner;
        
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 100 } },
          (decodedText) => {
            scanner.stop().catch(() => {});
            onScan(decodedText);
          },
          () => {}
        );
      } catch (_err) {
        setError('Camera not available. Use manual entry instead.');
        setMode('manual');
      }
    };
    
    startScanner();
    
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, [mode, onScan]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      onScan(manualCode.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="barcode-scanner-modal">
      <div className="bg-card rounded-xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            {mode === 'camera' ? <Camera className="h-5 w-5" /> : <Keyboard className="h-5 w-5" />}
            {mode === 'camera' ? 'Scan Barcode' : 'Enter Barcode'}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setMode(mode === 'camera' ? 'manual' : 'camera')}
              className="text-xs px-2 py-1 border rounded hover:bg-muted" data-testid="toggle-scan-mode">
              {mode === 'camera' ? 'Manual Entry' : 'Use Camera'}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {mode === 'camera' ? (
          <div className="p-4">
            {error && <p className="text-sm text-red-500 mb-2">{error}</p>}
            <div id="barcode-scanner-container" className="w-full rounded-lg overflow-hidden" style={{minHeight: '250px'}} />
            <p className="text-xs text-muted-foreground text-center mt-2">Point camera at barcode</p>
          </div>
        ) : (
          <form onSubmit={handleManualSubmit} className="p-4">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Type or scan barcode number..."
              className="w-full px-4 py-3 border rounded-lg text-lg font-mono text-center"
              autoFocus
              data-testid="manual-barcode-input"
            />
            <button type="submit"
              className="w-full mt-3 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
              data-testid="manual-barcode-submit">
              Look Up
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BarcodeScanner;
